/**
 * @fileoverview Pure week-grid computation extracted from WeeklyTimesheet.tsx
 * (MOR-906). No React, no Convex — every function here takes plain data in
 * and returns plain data out, so it's covered by direct unit tests rather
 * than DOM interaction tests.
 */
import { addDays, addMinutes, format, parseISO } from "date-fns";
import type { Id } from "@cvx/_generated/dataModel";
import { checkNarrative, type NarrativeWarning } from "./narrativeCheck";

export type EntryStatus = "draft" | "submitted" | "approved" | "rejected";
export type ProjectCode = "pre" | "post";
export type CalendarPeriod = "day" | "week" | "month" | "all";

export interface WeekEntry {
  _id: Id<"timeEntries">;
  /** "job" = matter time; "firm" = non-chargeable firm activity (no matter). */
  kind: "job" | "firm";
  /** Absent for firm activities, which carry no matter. */
  jobId?: Id<"jobs">;
  jobName: string;
  jobCode?: string;
  date: string;
  startTime?: string;
  endTime?: string;
  categoryId: Id<"timeCategories">;
  categoryName: string;
  subCategory?: string;
  narrative: string;
  units: number;
  billable: boolean;
  /** Absent for firm activities, which carry no matter phase. */
  projectCode?: ProjectCode;
  status: EntryStatus;
  hourlyRate?: number;
  // How `hourlyRate` was arrived at, when it is not simply the grade rate
  // (MOR-1018, MOR-1038). Rendered by `RateWorkingNote`.
  baseHourlyRate?: number;
  rateSource?: "grade" | "override";
  rateCardName?: string;
  multiplier?: number;
  ratesAsAt?: string;
  rejectionReason?: string;
}

/**
 * One day of the strip measured against the person's schedule (MOR-1459).
 * Only days that expect time are listed; a day off has no balance.
 */
export interface DayBalance {
  day: string;
  expectedUnits: number;
  recordedUnits: number;
  /** Shortfall on a day that has passed. 0 for today and later. */
  missingUnits: number;
}

export interface EntryFilters {
  search: string;
  jobIds: string[];
  categoryIds: string[];
  statuses: string[];
  billable: string[];
}

export interface SubmitPeriodInput {
  period: CalendarPeriod;
  historyStart: string;
  historyEnd: string;
  monthStart: string;
  monthEnd: string;
  selectedDate: string;
  weekStart: string;
  weekEnd: string;
}

/** yyyy-MM-dd for a Date. */
export function isoDate(d: Date): string {
  // QA-54: one unreproduced crash, "date.constructor is not a constructor",
  // came out of date-fns here. That error is what date-fns raises for a Date
  // built in another realm (an iframe or automation context), whose
  // `constructor` is not this window's `Date`. Rebuilding it costs nothing
  // and keeps the whole timesheet route from unmounting.
  if (!(d instanceof Date)) return format(new Date(d), "yyyy-MM-dd");
  return format(d, "yyyy-MM-dd");
}

/** HH:MM plus a duration, wrapping at midnight. Used to pre-fill the Log
 *  time dialog's end time from a calendar drag. */
export function endTimeFromStart(
  startTime: string,
  durationMin: number,
): string {
  const [h, m] = startTime.split(":").map(Number);
  return format(addMinutes(new Date(2000, 0, 1, h, m), durationMin), "HH:mm");
}

/** The Mon–Sun run of ISO dates starting at `weekStart` (which is assumed
 *  to already be a Monday). */
export function computeWeekDays(weekStart: string): string[] {
  const start = parseISO(weekStart);
  return Array.from({ length: 7 }, (_, i) => isoDate(addDays(start, i)));
}

/** Groups entries by their `date` field, preserving input order within
 *  each day. */
export function groupEntriesByDay<T extends { date: string }>(
  entries: T[],
): Map<string, T[]> {
  const map = new Map<string, T[]>();
  for (const e of entries) {
    const list = map.get(e.date) ?? [];
    list.push(e);
    map.set(e.date, list);
  }
  return map;
}

/** Builds a single filter predicate for the search box + faceted filters.
 *  Generic so team-scope entries (which carry extra fields like userName)
 *  keep their type through the filter. */
export function buildEntryFilter(filters: EntryFilters) {
  const search = filters.search.trim().toLowerCase();
  const jobSet = filters.jobIds.length > 0 ? new Set(filters.jobIds) : null;
  const categorySet =
    filters.categoryIds.length > 0 ? new Set(filters.categoryIds) : null;
  const statusSet =
    filters.statuses.length > 0 ? new Set(filters.statuses) : null;
  const billableSet =
    filters.billable.length > 0 ? new Set(filters.billable) : null;
  return <T extends WeekEntry>(list: T[]): T[] =>
    list.filter((e) => {
      if (jobSet && !jobSet.has(e.jobId as string)) return false;
      if (categorySet && !categorySet.has(e.categoryId as string)) {
        return false;
      }
      if (statusSet && !statusSet.has(e.status)) return false;
      if (
        billableSet &&
        !billableSet.has(e.billable ? "billable" : "non_billable")
      ) {
        return false;
      }
      if (
        search &&
        !`${e.narrative} ${e.jobName} ${e.jobCode ?? ""} ${e.categoryName}`
          .toLowerCase()
          .includes(search)
      ) {
        return false;
      }
      return true;
    });
}

/** Entries whose `date` falls within `[start, end]` inclusive (ISO string
 *  comparison, so callers must pass yyyy-MM-dd on both sides). */
export function entriesInRange<T extends { date: string }>(
  entries: T[],
  start: string,
  end: string,
): T[] {
  return entries.filter((e) => e.date >= start && e.date <= end);
}

/** The [start, end] ISO range that "Submit {period}" and the period total
 *  speak about, given the active period. */
export function computeSubmitRange(input: SubmitPeriodInput): {
  start: string;
  end: string;
} {
  const { period } = input;
  if (period === "all") {
    return { start: input.historyStart, end: input.historyEnd };
  }
  if (period === "month") {
    return { start: input.monthStart, end: input.monthEnd };
  }
  if (period === "day") {
    return { start: input.selectedDate, end: input.selectedDate };
  }
  return { start: input.weekStart, end: input.weekEnd };
}

/** Count of draft/rejected entries within [start, end] eligible for
 *  submission. "all" period never offers a submit action. */
export function computeSubmittableCount(
  entries: WeekEntry[],
  period: CalendarPeriod,
  start: string,
  end: string,
): number {
  if (period === "all") return 0;
  return entriesInRange(entries, start, end).filter(
    (e) => e.status === "draft" || e.status === "rejected",
  ).length;
}

export interface FlaggedNarrative {
  entry: WeekEntry;
  warning: NarrativeWarning;
}

/**
 * The entries a period submit would send whose narrative trips the ARITA
 * prompt (MOR-1039) — same draft/rejected, same range as
 * `computeSubmittableCount`, so the review lists exactly what is about to go.
 */
export function computeFlaggedNarratives(
  entries: WeekEntry[],
  period: CalendarPeriod,
  start: string,
  end: string,
  unitMinutes: number,
  /** The firm's own guidance (MOR-1039), appended to each warning message. */
  referenceText?: string,
): FlaggedNarrative[] {
  if (period === "all") return [];
  return entriesInRange(entries, start, end)
    .filter((e) => e.status === "draft" || e.status === "rejected")
    .flatMap((entry) => {
      const warning = checkNarrative(
        entry.narrative,
        entry.units,
        unitMinutes,
        referenceText,
      );
      return warning ? [{ entry, warning }] : [];
    });
}

/** Sum of units across every entry (any status) within [start, end]. */
export function computePeriodUnits(
  entries: WeekEntry[],
  start: string,
  end: string,
): number {
  return entriesInRange(entries, start, end).reduce(
    (sum, e) => sum + e.units,
    0,
  );
}

/**
 * Each scheduled day of the strip against what was recorded (MOR-1459).
 * `expectedByDay` is the person's schedule for the week from
 * `missingTimesheets.myExpectedUnits`: a day absent from it (weekend, day
 * off, public holiday, before they joined) is not measured. The shortfall is
 * only reported for days before today; today is still being worked.
 */
export function computeDayBalances(
  weekDays: string[],
  todayIso: string,
  entriesByDay: Map<string, WeekEntry[]>,
  expectedByDay: ReadonlyMap<string, number>,
): DayBalance[] {
  return weekDays.flatMap((day) => {
    const expectedUnits = expectedByDay.get(day) ?? 0;
    if (expectedUnits <= 0) return [];
    const recordedUnits = (entriesByDay.get(day) ?? []).reduce(
      (sum, e) => sum + e.units,
      0,
    );
    return [
      {
        day,
        expectedUnits,
        recordedUnits,
        missingUnits:
          day < todayIso ? Math.max(0, expectedUnits - recordedUnits) : 0,
      },
    ];
  });
}
